(ns app.front.view.application-wizard.location
  "Location step for the application wizard. Supports property selection
  through address/PIN search and map click, with a right-side detail panel
  showing the selected property and its attributes."
  (:require ["@carbon/icons-react/es/close/24" :default Close24]
            [app.domain.address.model :as address-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.municipality :as municipality]
            [app.front.ui :as ui]
            [app.front.ui.multi-select :as ui-ms]
            [app.front.ui.select :as ui-select]
            [app.front.ui.v2.detail-row :as ui-v2-dr]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.application-wizard.core :as-alias application-wizard]
            [app.front.view.application-wizard.subs :as subs]
            [app.front.view.create-application.gis-search :as gis-search]
            [app.front.view.map-common.util :as map-common-util]
            [app.front.view.property.attribute :as property-attribute]
            [app.front.view.property.units-editor :as units-editor]
            [app.front.view.wizard.property-selector :as property-selector]
            [app.front.view.workspace.address :as workspace-address]
            [app.front.view.workspace.location :as workspace-location]
            [app.front.view.workspace.map :as workspace-map]
            [app.i18n.model :as i18n-model]
            [cljs.reader :as reader]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.routing :as routing]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(defn- get-subdivision-from-route
  "Retrieves the subdivision (or country as fallback) from the municipality
  entity identified by route query params :country and :municipality."
  [db]
  (let [ctx          (entities/db->context db)
        country      (-> db :route/match :parameters :query :country)
        muni-number  (-> db :route/match :parameters :query :municipality)
        municipality (when (and country muni-number)
                       (entities/find-entity ctx {:municipality/country country
                                                  :municipality/number  muni-number}))]
    (or (:municipality/subdivision municipality) country)))

(rf/reg-sub ::subdivision
  :-> get-subdivision-from-route)

(defn- feature->location-data [db feature]
  (let [muni-number (-> db :route/match :parameters :query :municipality)
        subdivision (get-subdivision-from-route db)]
    (property-selector/make-location muni-number
                                     subdivision
                                     feature)))

(def ^:private query-location-keys
  (remove #{:municipality} property-selector/location-keys-without-project))

(def ^:private manual-input-value-keys
  [:property/id :property/legal :location/description])

(defn- location-from-query-params [query]
  (-> (select-keys query property-selector/location-keys-without-project)
      (update :address #(some-> % reader/read-string))
      (update :geometry #(some-> % reader/read-string))
      (update :gis-id #(some-> % reader/read-string))))

(defn- manual-input-from-query-params [location-data manual-entry? manual-values]
  (let [values (select-keys (if manual-values
                              (reader/read-string manual-values)
                              location-data)
                            manual-input-value-keys)]
    ;; Older URLs lack the manual-entry flag, so retain their value-based inference.
    (when (or manual-entry?
              (and (seq values)
                   (or (:location/description values)
                       (nil? (:property/integration-id location-data)))))
      (p/assoc-if {:enabled? true}
                  :values (not-empty values)))))

(defn- location-query-update [location-data]
  ;; Never write :municipality (entry param managed by core) or :subdivision
  ;; (derived from :country and :municipality) from a location selection.
  (merge {:manual-entry? nil :manual-values nil}
         (zipmap query-location-keys (repeat nil))
         (select-keys (dissoc location-data :municipality :subdivision) query-location-keys)))

(defn- manual-query-update [db enabled?]
  {:manual-entry? (when enabled? true)
   :manual-values (when enabled?
                    (pr-str (select-keys (get-in db [::manual-input :values]) manual-input-value-keys)))})

(defn- selection-query-update [db location-data manual?]
  (if manual?
    (merge (-> location-data
               (assoc :address (::manual-address-draft db))
               location-query-update)
           (manual-query-update db true))
    (location-query-update location-data)))

(defn- geometry->property-feature [geometry]
  (let [coords (:coordinates geometry)]
    (case (:type geometry)
      "Polygon"      (map-common-util/polygon-feature coords)
      "MultiPolygon" (map-common-util/multi-polygon-feature coords)
      (map-common-util/line-feature coords))))

(defn- location-data->property-feature [location-data]
  (some-> location-data
          :geometry
          geometry->property-feature))

(defn- point-search-placeholder [db [x y]]
  (let [muni-number (-> db :route/match :parameters :query :municipality)
        subdivision (get-subdivision-from-route db)]
    {:x            x
     :y            y
     :address      {}
     :municipality muni-number
     :subdivision  subdivision}))

(defn- municipality-for-event
  "Sync twin of `::application-wizard/municipality` sub (plus :country) for use inside
  event handlers (which cannot subscribe)."
  [db]
  (let [query (-> db :route/match :parameters :query)]
    {:country      (:country query)
     :municipality (:municipality query)}))

(defn propertyless-manual-location? [enhanced-pw? location-data]
  (and enhanced-pw? (str/blank? (:property/id location-data))))

(defn- location-completed?
  [location-data search-pending? manual-input? manual-values enhanced-pw? required-attrs]
  (boolean
    (and (not search-pending?)
         (cond
           manual-input?
           (and (:x location-data)
                (:y location-data)
                (if (propertyless-manual-location? enhanced-pw? location-data)
                  (-> manual-values :location/description str-util/blank->nil)
                  (every? #(-> manual-values % str-util/blank->nil) required-attrs)))

           location-data
           (-> location-data :property/id str-util/blank->nil)))))

(defn- location-saveable? [db]
  (let [location-data          (::location-data db)
        manual-input           (::manual-input db)
        {:keys [permit-type
                workspace-id]} (-> db :route/match :parameters :query)
        search-pending?        (or (get-in db [::gis-search/address-search-results :pending?])
                                   (::point-search-pending? db))
        domain-group           (when-let [group-id (subs/domain-group-id db)]
                                 (entities/find-entity (entities/db->context db) {:group/id group-id}))
        required-attrs         (property-model/required-property-input-keys
                                 (get-subdivision-from-route db) permit-type domain-group)
        enhanced-pw?           (ptc-model/permit-types-with-row-layers permit-type)]
    (and workspace-id
         (location-completed? location-data
                              search-pending?
                              (:enabled? manual-input)
                              (:values manual-input)
                              enhanced-pw?
                              required-attrs))))

(def ^:private cancel-autosave-location-fx
  {:dispatch-debounce [{:id     ::autosave-location
                        :action :cancel}]})

(defn- autosave-location-fx [db]
  (if (location-saveable? db)
    {:dispatch          [::application-wizard/autosave-location]
     :dispatch-debounce [{:id     ::autosave-location
                          :action :cancel}]}
    cancel-autosave-location-fx))

(defn- debounced-autosave-location-fx [db]
  (if (location-saveable? db)
    {:dispatch-debounce [{:id      ::autosave-location
                          :timeout 500
                          :action  :dispatch
                          :event   [::application-wizard/autosave-location]}]}
    cancel-autosave-location-fx))

(defn should-fetch-property-owners?
  [db domain-group-id feature location-data]
  (let [show-owners? (:show-owners-in-location-phase?
                       (subs/authority-group db))]
    (and (municipality/check-owners? db domain-group-id (:property feature))
         show-owners?
         (not= (:gis-layer location-data) :right-of-way-search))))

(defn- existing-units-request-args
  "Returns request args for ::fetch-existing-property-units, or nil if the fetch should not
  be made (road property or missing property id/municipality)."
  [db location-data]
  (when (not= :property.type/road (:property/type location-data))
    (when-let [verified-id (str-util/blank->nil (:property/id location-data))]
      (let [{:keys [country municipality]} (municipality-for-event db)]
        (when (and country municipality)
          {:country      country
           :municipality municipality
           :verified-id  verified-id})))))

(defn- feature-selection-effects
  "Common :db updates and :dispatch-n for selecting a property from search
  or point-search response. Both events differ only in how they update the
  map markers/zoom afterwards."
  [db feature manual?]
  (let [domain-group-id       (subs/domain-group-id db)
        location-data         (feature->location-data db feature)
        property-feature      (location-data->property-feature location-data)
        existing-units-params (existing-units-request-args db location-data)
        db'                   (-> db
                                  (assoc ::location-data location-data
                                         ::gis-error? false
                                         ::manual-input nil
                                         ::manual-address nil
                                         ::existing-property-units nil
                                         ::associate-units? false
                                         ::selected-existing-unit-ids nil)
                                  (update ::units-multi-select-generation (fnil inc 0))
                                  (assoc-in [::map :selected-property] property-feature))]
    (merge
      {:db db'
       :fx (cond-> [[:dispatch [::routing/merge-query-params
                                (selection-query-update db location-data manual?) :route/replace]]
                    [:dispatch [::property-selector/clear-property-metadata]]
                    [:dispatch [::property-selector/fetch-property-attributes domain-group-id feature]]
                    [:dispatch [::units-editor/clear-pending-new-property-units]]]
             (should-fetch-property-owners? db domain-group-id feature location-data)
             (conj [:dispatch [::property-selector/fetch-property-owners domain-group-id feature]])
             existing-units-params
             (conj [:dispatch [::fetch-existing-property-units existing-units-params]]))}
      (autosave-location-fx db'))))

(rf/reg-event-fx ::fetch-existing-property-units
  (fn [_ [_ {:keys [country municipality verified-id]}]]
    {:comm/command {:command    :property/find-existing-property-units
                    :method     :get
                    :data       {:country      country
                                 :municipality municipality
                                 :verified-id  verified-id}
                    :on-success [::fetch-existing-property-units.success]}}))

(rf/reg-event-fx ::fetch-existing-property-units.success
  (fn [{:keys [db]} [_ status {:keys [property-exists? units]}]]
    {:db       (assoc db ::existing-property-units {:property-exists? property-exists?
                                                    :unit-ids         (keys units)})
     :dispatch [::entities/merge-entity-db status units]}))

(rf/reg-sub ::existing-property-units
  :-> ::existing-property-units)

(rf/reg-sub ::existing-property-unit-items
  :<- [::existing-property-units]
  :<- [::entities/context]
  (fn [[{:keys [unit-ids]} context] _]
    (vec (entities/find-entities context (set unit-ids)))))

(rf/reg-event-db ::set-selected-existing-unit-ids
  (fn [db [_ ids]]
    (assoc db ::selected-existing-unit-ids ids)))

(rf/reg-event-db ::add-selected-existing-unit-id
  (fn [db [_ id]]
    (-> db
        (update ::selected-existing-unit-ids (fnil conj []) id)
        (update ::units-multi-select-generation (fnil inc 0)))))

(rf/reg-event-db ::remove-selected-existing-unit-id
  (fn [db [_ id]]
    (-> db
        (update ::selected-existing-unit-ids
                (fn [ids] (vec (remove #(= % id) ids))))
        (update ::units-multi-select-generation (fnil inc 0)))))

(rf/reg-sub ::selected-existing-unit-ids
  :-> ::selected-existing-unit-ids)

(rf/reg-sub ::selected-existing-units
  :<- [::selected-existing-unit-ids]
  :<- [::entities/context]
  (fn [[ids context] _]
    (vec (entities/find-entities context (set ids)))))

(rf/reg-sub ::units-multi-select-generation
  :-> ::units-multi-select-generation)

(rf/reg-event-db ::set-associate-units?
  (fn [db [_ associate-units?]]
    (assoc db ::associate-units? associate-units?)))

(rf/reg-sub ::associate-units?
  (fn [db _]
    (boolean (::associate-units? db))))

(rf/reg-event-fx ::clear-property
  (fn [{:keys [db]} _]
    (merge {:db (-> db
                    (assoc ::location-data nil
                           ::gis-error? false
                           ::manual-input nil
                           ::manual-address nil
                           ::point-search-pending? false
                           ::existing-property-units nil
                           ::associate-units? false
                           ::selected-existing-unit-ids nil)
                    (dissoc ::manual-entry-requested? ::manual-address-draft)
                    (update ::units-multi-select-generation (fnil inc 0))
                    (assoc-in [::map :selected-property] nil)
                    (assoc-in [::map :markers] []))
            :fx [[:dispatch [::property-selector/clear-property-metadata]]
                 [:dispatch [::units-editor/clear-pending-new-property-units]]
                 [:dispatch [::routing/merge-query-params (location-query-update nil) :route/replace]]]}
           cancel-autosave-location-fx)))

(rf/reg-event-fx ::cleanup
  (fn [{:keys [db]} _]
    (merge {:db         (dissoc db
                                ::location-data ::manual-input ::manual-address ::manual-address-draft
                                ::manual-entry-requested?
                                ::gis-error? ::point-search-pending? ::map ::layer-id-order
                                ::existing-property-units ::gis-search/address-search-results
                                ::associate-units? ::selected-existing-unit-ids
                                ::units-multi-select-generation)
            :dispatch-n [[::property-selector/clear-property-metadata]
                         [::units-editor/clear-pending-new-property-units]]}
           cancel-autosave-location-fx)))

(defn- prepare-pw-manual-location [db]
  (let [location-data (::location-data db)
        address       (or (::manual-address-draft db) (::manual-address db) (:address location-data) {})]
    (-> db
        (assoc ::location-data (assoc (select-keys location-data [:x :y :municipality :subdivision])
                                      :address address)
               ::manual-address address
               ::manual-address-draft address
               ::gis-error? false
               ::existing-property-units nil
               ::associate-units? false
               ::selected-existing-unit-ids nil)
        (update-in [::manual-input :values] select-keys [:location/description])
        (update ::units-multi-select-generation (fnil inc 0))
        (assoc-in [::map :selected-property] nil))))

(rf/reg-event-fx ::set-manual-input-enabled
  (fn [{:keys [db]} [_ enabled?]]
    (when-not (and enabled? (get-in db [::manual-input :enabled?]))
      (let [flag? (ff/enabled? :feature-flag.id/application-wizard-location-manual-entry)
            pw?   (and enabled? flag? (= :permit-type/PW (get-in db [:route/match :parameters :query :permit-type])))
            db    (if pw? (prepare-pw-manual-location db) db)
            db'   (if enabled?
                    (let [location-data (::location-data db)]
                      (-> db
                          (assoc ::manual-entry-requested? true)
                          (assoc-in [::manual-input :enabled?] true)
                          (update-in [::manual-input :values]
                                     merge
                                     (select-keys location-data [:property/id :property/legal :location/description]))
                          (assoc ::manual-address (or (::manual-address db)
                                                      (:address location-data)
                                                      {}))
                          (assoc ::manual-address-draft (or (::manual-address db)
                                                            (::manual-address-draft db)
                                                            (:address location-data)
                                                            {}))))
                    (-> db
                        (assoc-in [::manual-input :enabled?] false)
                        (dissoc ::manual-entry-requested? ::manual-address-draft)))]
        (merge (p/assoc-if
                 {:db db'}
                 :fx (when flag?
                       (cond-> [[:dispatch [::routing/merge-query-params
                                            (if pw?
                                              (selection-query-update db' (::location-data db') true)
                                              (manual-query-update db' enabled?)) :route/replace]]]
                         pw? (into [[:dispatch [::property-selector/clear-property-metadata]]
                                    [:dispatch [::units-editor/clear-pending-new-property-units]]
                                    [:dispatch [::units-editor/close-new-unit-form]]]))))
               (debounced-autosave-location-fx db'))))))

(rf/reg-event-fx ::set-manual-input-value
  (fn [{:keys [db]} [_ k v]]
    (let [db' (assoc-in db [::manual-input :values k] v)]
      (merge {:db db'
              :fx [[:dispatch [::routing/merge-query-params
                               (if (ff/enabled? :feature-flag.id/application-wizard-location-manual-entry)
                                 (manual-query-update db' (get-in db' [::manual-input :enabled?]))
                                 {k (str-util/blank->nil v)})
                               :route/replace]]]}
             (debounced-autosave-location-fx db')))))

(rf/reg-event-fx ::set-manual-address
  (fn [{:keys [db]} [_ value]]
    (let [db' (cond-> (assoc db ::manual-address value)
                (and (ff/enabled? :feature-flag.id/application-wizard-location-manual-entry)
                     (::manual-entry-requested? db))
                (assoc ::manual-address-draft value))]
      (merge {:db       db'
              :dispatch [::routing/merge-query-params {:address (not-empty value)} :route/replace]}
             (debounced-autosave-location-fx db')))))

(rf/reg-event-db ::set-gis-error
  (fn [db [_ v]]
    (assoc db ::gis-error? v)))

(rf/reg-sub ::location-data
  :-> ::location-data)

(rf/reg-event-db ::set-location-data
  (fn [db [_ location-data]]
    (cond-> (assoc db ::location-data location-data)
      (:x location-data)
      (-> (assoc-in [::map :center] [(:x location-data) (:y location-data)])
          (assoc-in [::map :zoom] 18)
          (assoc-in [::map :markers] [(map-common-util/point-feature #js [(:x location-data) (:y location-data)])])))))

(rf/reg-event-fx ::restore-from-query
  (fn [{:keys [db]} [_ query]]
    ;; Controllers can restart while navigating between steps. The in-memory
    ;; selection is newer than the URL in that case and must take precedence.
    (when-not (::location-data db)
      (let [location-data (location-from-query-params query)
            manual-input  (manual-input-from-query-params
                            location-data (:manual-entry? query) (:manual-values query))]
        (when (seq location-data)
          {:db       (p/assoc-if db
                                 ::manual-input manual-input
                                 ::manual-entry-requested? (when manual-input true)
                                 ::manual-address-draft (when manual-input (:address location-data))
                                 ::manual-address (when manual-input (:address location-data)))
           :dispatch [::set-location-data location-data]})))))

(rf/reg-sub ::point-search-pending?
  :-> ::point-search-pending?)

(rf/reg-sub ::gis-error?
  :-> ::gis-error?)

(rf/reg-sub ::manual-input
  :-> ::manual-input)

(rf/reg-sub ::manual-input?
  :<- [::manual-input]
  :-> :enabled?)

(rf/reg-sub ::manual-input-values
  :<- [::manual-input]
  :-> :values)

(rf/reg-sub ::manual-address
  :-> ::manual-address)

(rf/reg-sub ::address-result-pending
  :<- [::gis-search/pending?]
  :<- [::point-search-pending?]
  (fn [[addr-pending? point-pending?] _]
    (or addr-pending? point-pending?)))

(rf/reg-sub ::address-search-term
  :<- [::gis-search/address-search-results]
  :-> :search-value)

(rf/reg-sub ::required-attrs
  :<- [::subdivision]
  :<- [::subs/permit-type]
  :<- [::subs/domain-group]
  (fn [[subdivision permit-type domain-group] _]
    (property-model/required-property-input-keys subdivision permit-type domain-group)))

(rf/reg-sub ::attr-required?
  :<- [::required-attrs]
  (fn [required-attrs [_ attr]]
    (contains? required-attrs attr)))

(rf/reg-sub ::enhanced-pw?
  :<- [::subs/permit-type]
  :-> ptc-model/permit-types-with-row-layers)

(rf/reg-sub ::step-completed?
  :<- [::location-data]
  :<- [::address-result-pending]
  :<- [::manual-input?]
  :<- [::manual-input-values]
  :<- [::enhanced-pw?]
  :<- [::required-attrs]
  :<- [::application-wizard/needs-municipality-selection?]
  (fn [[location-data search-pending? manual-input? manual-values enhanced-pw?
        required-attrs needs-municipality-selection?] _]
    ;; Location step is not complete until the user has selected a municipality
    ;; (when the authority group serves multiple municipalities).
    (and (not needs-municipality-selection?)
         (location-completed? location-data search-pending? manual-input? manual-values enhanced-pw? required-attrs))))

(rf/reg-sub ::municipal-layers
  :<- [::subs/domain-group-id]
  :<- [::municipality/municipality-config]
  :<- [::municipality/map-tokens]
  :<- [::subs/permit-type]
  (fn [[domain-group-id config tokens permit-type] _]
    (municipality/municipal-layers config tokens domain-group-id false (ptc-model/permit-types-with-row-layers permit-type))))

(rf/reg-sub ::layer-id-order
  :-> ::layer-id-order)

(rf/reg-event-db ::set-layer-id-order
  (fn [db [_ layer-ids]]
    (assoc db ::layer-id-order layer-ids)))

(rf/reg-sub ::custom-center
  #(-> % ::map :center))


(rf/reg-sub ::map-zoom
  (fn [db _]
    (-> db ::map :zoom)))



(rf/reg-sub ::map-center
  :<- [::custom-center]
  :<- [::subs/domain-group-id]
  :<- [::municipality/municipality-config]
  (fn [[custom-center domain-group-id config] _]
    (or custom-center
        (get-in config [domain-group-id :map-center]))))

(rf/reg-sub ::show-property-owners-for-location?
  :<- [::subs/authority-group]
  :-> :show-owners-in-location-phase?)

(rf/reg-sub ::property-owners-for-location
  (fn [[_ owner-key] _]
    [(rf/subscribe [::show-property-owners-for-location?])
     (rf/subscribe [::property-selector/property-owners owner-key])])
  (fn [[show-owners owners] _]
    (when show-owners
      owners)))

(rf/reg-event-fx ::select-property-from-search
  (fn [{:keys [db]} [_ feature]]
    (let [{:keys [x y]} (feature->location-data db feature)]
      (cond-> (update (feature-selection-effects db feature false) :db dissoc
                      ::manual-entry-requested? ::manual-address-draft)
        (and x y)
        (update :db
                #(-> %
                     (assoc-in [::map :markers] [(map-common-util/point-feature #js [x y])])
                     (assoc-in [::map :center] [x y])
                     (assoc-in [::map :zoom] 18)))))))

(defn- restore-manual-entry-effects [effects db manual?]
  (let [db' (-> (:db effects)
                (assoc ::manual-input (assoc (::manual-input db) :enabled? manual?)
                       ::manual-address (when manual? (::manual-address-draft db))))]
    (merge effects {:db db'} (autosave-location-fx db'))))

(rf/reg-event-fx ::point-search.success
  (fn [{:keys [db]} [_ _ response]]
    (let [manual-entry? (and (ff/enabled? :feature-flag.id/application-wizard-location-manual-entry)
                             (::manual-entry-requested? db))
          manual?       (and manual-entry? (str/blank? (get-in response [:property :property/id])))
          response      (if manual?
                          (assoc response :address (::manual-address-draft db))
                          response)
          effects       (feature-selection-effects (assoc db ::point-search-pending? false) response manual?)]
      (if manual-entry?
        (restore-manual-entry-effects effects db manual?)
        effects))))


(rf/reg-event-fx ::point-search.not-found
  (fn [{:keys [db]} [_ [x y] gis-error?]]
    (let [manual?       (and (ff/enabled? :feature-flag.id/application-wizard-location-manual-entry)
                             (::manual-entry-requested? db))
          location-data (cond-> (point-search-placeholder db [x y])
                          manual? (assoc :address (::manual-address-draft db)))
          db'           (-> db
                            (assoc ::point-search-pending? false)
                            (assoc ::gis-error? gis-error?
                                   ::location-data location-data
                                   ::manual-input (when manual?
                                                    (assoc (::manual-input db) :enabled? true))
                                   ::manual-address (when manual? (::manual-address-draft db)))
                            (assoc-in [::map :selected-property] nil)
                            (assoc-in [::map :markers] [(map-common-util/point-feature #js [x y])])
                            (assoc-in [::map :center] [x y]))]
      (merge
        {:db db'
         :fx [[:dispatch [::property-selector/clear-property-metadata]]
              [:dispatch [::routing/merge-query-params
                          (selection-query-update db location-data manual?) :route/replace]]]}
        (autosave-location-fx db')))))


(rf/reg-event-fx ::map-click
  (fn [{:keys [db]} [_ ^js map-click]]
    (let [click-coordinates     (.-coordinate map-click)
          domain-group-id       (subs/domain-group-id db)
          selected-municipality (-> db :route/match :parameters :query :municipality)
          layer-ids             (if (ff/enabled? :feature-flag.id/public-works-location-enhancements)
                                  (::layer-id-order db)
                                  [])]
      (p/assoc-if
        {:comm/command (property-selector/point-search-command
                         click-coordinates
                         {:domain-group-id       domain-group-id
                          :selected-municipality selected-municipality
                          :layer-ids             layer-ids}
                         [::point-search.success]
                         [::point-search.not-found])
         :db           (-> db
                           (assoc ::point-search-pending? true)
                           (cond-> (not (ff/enabled? :feature-flag.id/application-wizard-location-manual-entry))
                             (assoc ::gis-error? false
                                    ::location-data nil
                                    ::manual-address nil))
                           (assoc-in [::map :markers] [(map-common-util/point-feature click-coordinates)])
                           (assoc-in [::map :center] click-coordinates))}
        :dispatch (when-not (ff/enabled? :feature-flag.id/application-wizard-location-manual-entry)
                    [::property-selector/clear-property-metadata])))))


;;; Load map config when entering the step

(rf/reg-event-fx ::enter-step
  (fn [{:keys [db]} _]
    (let [domain-group-id (subs/domain-group-id db)]
      (when (and domain-group-id
                 (not (municipality/map-config-loaded? db domain-group-id)))
        {:dispatch [::municipality/load-map-config domain-group-id]}))))

(rf/reg-event-fx ::search-and-select-location
  (fn [{:keys [db]} [_ location]]
    (let [domain-group-id       (subs/domain-group-id db)
          selected-municipality (-> db :route/match :parameters :query :municipality)]
      {:comm/command {:command          :address/search
                      :data             {:domain-group-id domain-group-id
                                         :municipalities  [selected-municipality]
                                         :term            location
                                         :layer-ids       []
                                         :max-results     1}
                      :on-success       [::search-and-select-location.success]
                      :finally-dispatch [::search-and-select-location.finally]}
       :db           (-> db
                         (assoc-in [::gis-search/address-search-results :search-value] location)
                         (assoc-in [::gis-search/address-search-results :pending?] true))})))

(rf/reg-event-fx ::search-and-select-location.success
  (fn [_ [_ _ results]]
    (when-let [first-result (first results)]
      {:dispatch [::select-property-from-search first-result]})))

(rf/reg-event-db ::search-and-select-location.finally
  (fn [db _]
    (assoc-in db [::gis-search/address-search-results :pending?] false)))

;;; Property detail rendering helpers

(def location-title :h4.mb-3.text-base.font-semibold)
(def location-item :p.mb-0.pb-0.text-sm)

(defn- property-summary-line [tr location]
  (cond
    (= :property.type/road (:property/type location))
    (or (:property/description location)
        (when (or (:property/from-st location) (:property/to-st location))
          (tr [:property/from-to]
              [(:property/from-st location)
               (:property/to-st location)])))
    :else
    (property-model/property-type-text tr location)))

(defn- property-detail-rows [tr location]
  (let [country (first (str/split (:municipality location) #"-"))]
    (filter second [(when (= :property.type/road (:property/type location))
                      [[:application.property/type] (tr [:application.property/road])])
                    [[:property/id] (:property/id location)]
                    [[:property/secondary-id] (:property/secondary-id location)]
                    [[:property/integration-id] (:property/integration-id location)]
                    [[:property/type-code]
                     (property-model/property-type-text tr (:property/type-code location) country)]])))

(defn- property-details [tr location]
  (when-let [rows (seq (property-detail-rows tr location))]
    [:div.mb-6
     (for-frag [[label-tr value] rows]
       [ui-v2-dr/detail-row tr label-tr value])]))

(defn- other-property-information [tr location]
  (let [property-owners (<sub [::property-owners-for-location (property-selector/owner-cache-key location)])
        attributes      (<sub [::property-selector/property-attributes])
        rows            (filter second
                                [[[:property/ward] (:property/ward location)]
                                 [[:property/zoning] (:property/zoning location)]
                                 [[:property/legal] (:property/legal location)]
                                 [(if (next property-owners)
                                    [:application.property/owners]
                                    [:application.property/owner])
                                  (when (seq property-owners)
                                    (->> property-owners
                                         (map :name)
                                         (str/join ", ")))]])]
    (when (or (seq rows) (seq attributes))
      [:div
       (when (seq rows)
         [:div.mb-6
          (for-frag [[label-tr value] rows]
            [ui-v2-dr/detail-row tr label-tr value])])
       (when (seq attributes)
         [:div
          [location-title (tr [:wizard/property-details])]
          [:div.mt-1 [property-attribute/attribute-tags attributes]]])])))

(defn- units-section [tr location]
  (let [lang         (<sub [:language])
        domain-group (<sub [::subs/domain-group])
        designators  (->> (:property-unit-designator/_group domain-group)
                          (filter (du/in-state-fn :property-unit-designator.state/active))
                          (sort-by #(property-model/unit-designator-name % lang)))]
    (when (and (ff/enabled? :feature-flag.id/property-db-units)
               (seq designators))
      (let [associate-units?  (<sub [::associate-units?])
            unit-items        (<sub [::existing-property-unit-items])
            selected-units    (<sub [::selected-existing-units])
            multi-select-gen  (<sub [::units-multi-select-generation])
            pending-new-units (<sub [::units-editor/pending-new-property-units])
            added-units       (concat selected-units
                                      (map (fn [pending]
                                             (assoc {:property-unit/identifier (:identifier pending)
                                                     :property-unit/designator (:unit-designator pending)}
                                                    ::pending-unit pending))
                                           pending-new-units))
            new-unit-open?    (<sub [::units-editor/new-unit-section-open?])
            ;; No property entity may exist yet at this point in the wizard, so build a minimal
            ;; stand-in with just the address for the new-unit form's read-only address reminder.
            properties        [{:property/primary-address (:address location)}]]
        [:div.mt-4
         [:div
          (cc/checkbox {:id         "associate-units-checkbox"
                        :label-text (tr [:wizard/associate-units])
                        :checked    associate-units?
                        :on-change  #(>evt [::set-associate-units? %])})]
         (when associate-units?
           [:div.-mx-4.mt-4.border-t.border-gray-300.border-solid.px-4.pt-4
            [location-title (tr [:wizard/units])]
            [:p.mt-1.text-sm.text-ui-night-sky-100 (tr [:wizard/select-units-help-text])]
            [:div.mt-4.flex.items-start.gap-4
             [:div.flex-1
              ^{:key (str "available-units-" multi-select-gen)}
              [ui-ms/multi-select
               {:id             "available-units-multi-select"
                :label-text     (tr [:wizard/available-units])
                :displayed-text (tr [(if (seq unit-items)
                                       :wizard/select-units
                                       :wizard/no-existing-units-found)])
                :disabled       (empty? unit-items)
                :values         unit-items
                :value          (set selected-units)
                :val->str       (fn [unit]
                                  (property-model/localized-unit-display-key
                                    unit
                                    #(property-model/unit-designator-name % lang)))
                :on-change      #(>evt [::set-selected-existing-unit-ids (mapv :db/id %)])}]]
             [:div.mt-6.flex.items-center.gap-4
              [:div.font-bold.uppercase.text-ui-night-sky-100 (tr [:wizard/or])]
              (cc/button {:kind     :tertiary
                          :size     :field
                          :on-click #(>evt [::units-editor/set-new-unit-section-open true])}
                         (tr [:wizard/add-new-unit]))]]
            (when new-unit-open?
              [:div.mt-4
               [units-editor/new-unit-fields tr lang nil properties designators
                :layout  :narrow
                :on-add  (fn [_property identifier unit-designator]
                           (when-let [existing (units-editor/matching-existing-unit unit-items identifier unit-designator)]
                             (>evt [::units-editor/close-new-unit-form])
                             (when-not (contains? (set (map :db/id selected-units)) (:db/id existing))
                               (>evt [::add-selected-existing-unit-id (:db/id existing)]))
                             true))]])
            (when (seq added-units)
              [:div.mt-4
               [units-editor/added-units-list tr lang added-units
                :on-remove (fn [unit]
                             (if-let [pending (::pending-unit unit)]
                               (>evt [::units-editor/remove-pending-new-property-unit pending])
                               (>evt [::remove-selected-existing-unit-id (:db/id unit)])))]])])]))))

(defn- address-editor [tr subdivision & {:keys [optional?]}]
  [workspace-address/address-input-fields
   (util/mmap {:set-addr-evt ::set-manual-address
               :optional?    (boolean optional?)
               :address      (<sub [::manual-address])}
              tr subdivision)])

(defn- clear-property-button [tr]
  [cc/icon-button {:kind             :ghost
                   :size             :small
                   :icon-description (tr [:close])
                   :render-icon      Close24
                   :on-click         #(>evt [::clear-property])}])

(defn- selected-location [tr location]
  (let [address-text (-> location :address :address/full-address)
        summary-line (property-summary-line tr location)]
    [:div.rounded.border.border-gray-300.border-solid.bg-ui-white
     {:data-guide-id          "selected-property"
      :data-guide-label       (tr [:wizard/property-details])
      :data-guide-description "Details of the selected property including address, owners, and attributes"}
     [:div.flex.items-start.justify-between.border-b.border-gray-300.border-solid.p-4
      [:div.pr-4
       [:h3.mb-1.text-xl.font-normal.text-ui-night-sky-100
        (or address-text
            (:property/id location)
            (tr [:wizard/property-details]))]
       (when summary-line
         [:p.mb-0.text-sm.text-ui-night-sky-100 summary-line])]
      [clear-property-button tr]]
     [:div.p-4
      (when (and (not= :property.type/road (:property/type location))
                 (address-model/address-checker (:address location)))
        [:div.mb-6
         [location-item (tr [:wizard/address-not-found])]
         [address-editor tr (-> location :subdivision)]])
      [property-details tr location]
      [other-property-information tr location]
      [units-section tr location]]]))

(defn- location-attr [tr attr values]
  [:div.mt-3
   [ui/text-input {:label-text (tr [attr])
                   :value      (get values attr)
                   :required   (<sub [::attr-required? attr])
                   :on-change  #(>evt [::set-manual-input-value attr (futil/tval %)])}]])

(defn- manual-entry-needs-point [tr]
  [:div.font-semibold.mb-6.mt-3.lg:mt-0
   (tr [:wizard/manual-entry-point-info])])

(defn- manual-entry-location-info [tr]
  [:div
   {:data-cy                "manual-location-warning"
    :data-guide-id          "manual-location-warning"
    :data-guide-label       (tr [:wizard/location])
    :data-guide-description "Select the property location on the map before continuing"}
   [ui/help-2
    {:class "mt-0 mb-6"}
    (tr [:wizard/set-property-location-description])]])

(defn- manual-address-entry [tr subdivision]
  (let [values (<sub [::manual-input-values])]
    [:div {:data-guide-id                 "manual-property-entry"
           :data-guide-label              (tr [:wizard/property-details])
           :data-guide-description        "Manually enter property ID and legal description"
           :data-guide-highlight-selector "input"}
     [:div.mb-6.mt-3.lg:mt-0
      [location-title (tr [:workspace/address])]
      [address-editor tr subdivision {:optional? true}]]
     [:div.mb-6
      [location-title (tr [:wizard/property-details])]
      [location-attr tr :property/id values]
      [location-attr tr :property/legal values]]]))

(defn- manual-location-entry [tr location-data subdivision]
  (let [values (<sub [::manual-input-values])]
    [:div {:data-guide-id                 "manual-location-entry"
           :data-guide-label              (tr [:wizard/location])
           :data-guide-description        "Manually describe the location and work area for permit"
           :data-guide-highlight-selector "input"}
     [:div.mb-6.mt-3.lg:mt-0
      [location-title (tr [:wizard/location])]
      [address-editor tr subdivision {:optional? true}]]
     [:div.mb-6
      [ui/text-area {:label-text (tr [:location/description])
                     :value      (get values :location/description)
                     :required   true
                     :on-change  #(>evt [::set-manual-input-value :location/description (futil/tval %)])}]]
     [workspace-location/coordinates tr location-data]]))

;;; Missing property / manual entry

(def ^:private link-button :button.bg-transparent.border-0.p-0.text-ui-blue.hover:underline)

(defn- manual-entry-link [tr]
  [:span
   {:data-guide-id          "manual-property-entry-link"
    :data-guide-label       (tr [:wizard/enter-address-manually])
    :data-guide-description "Open the fields for manually entering property details"}
   [:a.text-ui-blue.hover:underline
    {:data-cy  "manual-property-entry-link"
     :href     "#manual-property-entry"
     :on-click (fn [event]
                 (.preventDefault event)
                 (>evt [::set-manual-input-enabled true]))}
    (tr [:wizard/enter-address-manually])]])

(defn- missing-property-data [tr]
  (let [gis-error? (<sub [::gis-error?])]
    [:div.p-4 {:data-guide-id          "missing-property"
               :data-guide-label       (tr [(if gis-error?
                                              :wizard/gis-error-title
                                              :wizard/location-not-found-title)])
               :data-guide-description "Options to manually enter location when no GIS match is found"}
     [:div.mb-6
      [:h3 (tr [(if gis-error?
                  :wizard/gis-error-title
                  :wizard/location-not-found-title)])]
      [:p (tr [(if gis-error?
                 :wizard/gis-error-description
                 :wizard/location-not-found-description)])]
      [:p [link-button
           {:on-click #(>evt [::set-manual-input-enabled true])}
           (tr [:wizard/start-manual-entry])]]]]))

;;; Map wrapper

(rf/reg-sub ::default-map-center
  :<- [::municipality/municipalities]
  (fn [municipalities _]
    (->> municipalities
         (map :map-center)
         municipality-model/center-of-centers)))

(defn- map-wrapper [col-size]
  (let [default-map-center (<sub [::default-map-center])
        map-center         (or (<sub [::map-center])
                               default-map-center
                               [0 0])
        map-zoom           (or (<sub [::map-zoom]) 12)]
    [:div {:class-name (str "bx--col-xs-12 bx--col-lg-" col-size)
           :style      {:max-height "50vh" :min-height "400px"}}
     [workspace-map/workspace-map {:center             map-center
                                   :zoom               map-zoom
                                   :layers             (<sub [::municipal-layers])
                                   :propertyGeometries {:primaryPropertyGeometry
                                                        (when-let [location-data (<sub [::location-data])]
                                                          (property-model/geojson-feature (:geometry location-data)))}
                                   :extended?          (ff/enabled? :feature-flag.id/public-works-location-enhancements)
                                   :layerOpacity       0.7
                                   :setLayerIdOrder    #(>evt [::set-layer-id-order %])
                                   :events             [{:eventType     "singleclick"
                                                         :eventFunction #(>evt [::map-click %])}]}]]))

;;; Main location step

(defn- municipality-selector [tr ag-municipalities lang]
  [:div.mb-6
   {:data-guide-id          "municipality-select"
    :data-guide-label       (tr [:wizard/select-municipality])
    :data-guide-description "Select the municipality where the project is located"}
   [ui-select/select
    {:id           "wizard-municipality-select"
     :label-text   (tr [:wizard/select-municipality])
     :allow-empty? false
     :select-text  (tr [:municipalities/select-municipality])
     :values       ag-municipalities
     :val->str     #(i18n-model/get-text (:municipality/name %) lang)
     :on-change    #(>evt [::application-wizard/municipality-selected %])}]])


(defn- location-description-key [enhanced-pw? manual-entry-enabled?]
  (cond
    enhanced-pw?             :wizard/location-description-enhanced-pw
    manual-entry-enabled?    :wizard/location-description-manual-entry
    :else                    :wizard/location-description))

(defn description [tr]
  (let [description-key (location-description-key (<sub [::enhanced-pw?])
                                                  (ff/enabled? :feature-flag.id/application-wizard-location-manual-entry))]
    (tr [description-key]
        [(property-model/format-property-id-for-sentence tr)])))

(defn view [tr]
  (let [location-data              (<sub [::location-data])
        domain-group-id            (<sub [::subs/domain-group-id])
        search-pending?            (<sub [::address-result-pending])
        search-term                (<sub [::address-search-term])
        gis-error?                 (<sub [::gis-error?])
        manual-input?              (<sub [::manual-input?])
        result-visible?            (or (some? location-data) manual-input? gis-error?)
        subdivision                (<sub [::subdivision])
        municipality-name          (some-> (<sub [::application-wizard/municipality-name])
                                           (i18n-model/get-text-from-entities-or-map (<sub [:language])))
        lang                       (<sub [:language])
        needs-municipality-select? (<sub [::application-wizard/needs-municipality-selection?])
        ag-municipalities          (<sub [::application-wizard/authority-group-municipalities])
        missing-property-data?     (str/blank? (:property/id location-data))
        enhanced-pw?               (<sub [::enhanced-pw?])
        ;; Disable the GIS search and map until a municipality has been chosen.
        search-disabled?           (or (nil? domain-group-id) needs-municipality-select?)]

    [:div#application-wizard--location
     {:data-guide-page-description
      "Application wizard — Location step: Search for a property by address or PIN, click on the map, or manually enter property details."}

     ;; Municipality selector — shown when the group has multiple municipalities
     ;; and none has been selected yet.
     (when needs-municipality-select?
       [municipality-selector tr ag-municipalities lang])

     ;; Municipality info — shown after a municipality has been selected
     (when (and municipality-name (not needs-municipality-select?))
       [:div.mb-6
        [:div.text-base.font-semibold.text-ui-night-sky-100
         municipality-name]])

     (when-not needs-municipality-select?
       [:div
        ;; Address search — disabled until municipality is chosen for multi-muni groups
        (if (ff/enabled? :feature-flag.id/application-wizard-location-manual-entry)
          [:div.mb-2.flex.flex-wrap.items-center.gap-1.text-sm.text-ui-night-sky-100
           [:span (tr [:wizard/search-by-address-or-pin])]
           [:span "/"]
           [manual-entry-link tr]]
          [:div.mb-2.text-sm.text-ui-night-sky-100
           (tr [:wizard/search-by-address-or-pin])])
        [:div.rounded.border.border-solid.border-gray-400.bg-ui-white
         {:data-guide-id          "address-search"
          :data-guide-label       (tr [:wizard/search-by-address-or-pin])
          :data-guide-description "Search for a property by address, PIN, or click on the map"}
         [gis-search/address-search
          {:class              []
           :placeholder        (tr [:wizard/address-or-pin])
           :tr                 tr
           :light              true
           :domain-group-id    domain-group-id
           :disabled           search-disabled?
           :municipalities     [(<sub [::application-wizard/municipality])]
           :search-input-style {:border-bottom "none"}
           :on-select-address  #(>evt [::select-property-from-search %])
           :on-select-location #(>evt [::select-property-from-search %])
           :on-error-evt       [::set-gis-error true]
           :group-gis-layers?  true
           :layer-id-fn        ::layer-id-order
           :show-existing?     false}]]

        ;; Search pending / manual entry link
        (if (ff/enabled? :feature-flag.id/application-wizard-location-manual-entry)
          (when search-pending?
            [:div.mt-2
             (cc/inline-loading {:description (tr [:wizard/searching])})])
          [:div.mt-2 {:style {:min-height "32px"}}
           (when search-pending?
             (cc/inline-loading {:description (tr [:wizard/searching])}))
           (when (and (not search-pending?)
                      (not result-visible?)
                      (not needs-municipality-select?)
                      (string? search-term)
                      (> (count search-term) 5))
             [:div.mt-2
              [link-button
               {:on-click #(>evt [::set-manual-input-enabled true])}
               (tr [:wizard/location-not-found?])]])])

        [:div.bx--row.mt-4.mb-2
         (let [col-size (if result-visible? 6 12)]
           [map-wrapper col-size])
         (when result-visible?
           [:div.bx--col-xs-12.bx--col-lg-6
            [:div.h-full
             (cond
               manual-input?
               (if (ff/enabled? :feature-flag.id/application-wizard-location-manual-entry)
                 [:<>
                  (when-not (and (:x location-data) (:y location-data))
                    [manual-entry-location-info tr])
                  (if (propertyless-manual-location? enhanced-pw? location-data)
                    [manual-location-entry tr location-data subdivision]
                    [manual-address-entry tr subdivision])]
                 (cond
                   (not (:x location-data))
                   [manual-entry-needs-point tr]

                   (propertyless-manual-location? enhanced-pw? location-data)
                   [manual-location-entry tr location-data subdivision]

                   :else
                   [manual-address-entry tr subdivision]))

               (or missing-property-data?
                   gis-error?)
               [missing-property-data tr]

               :else
               [selected-location tr location-data])]])]])]))
